import { useEffect, useState } from 'react'

import { FilterResultModel, dataToExcel, textToPdf } from '@/Shared/domain'
import { useLoader } from '@/Shared/ui/loader'
import { TransactionsContract } from '@/Transactions/application'
import {
  ProductModel,
  TransactionFiltersModel,
  TransactionModel,
  TransactionsRepository,
} from '@/Transactions/domain'
import { TransactionFilterLocals } from '@/Transactions/domain/TransactionFilterLocals'
import { useUserData } from '@/User/ui/context'

export interface IUseTransactions {
  transactions?: TransactionModel[]
  clearTransactions: () => void
  filters?: FilterResultModel[]
  handleSearch: (data: TransactionFiltersModel) => void
  count: number
  page: number
  numberOfRecordsPerPage: number
  onPageChange: (page: number) => void
  downloadTransactions: () => void
  localsFilterData?: TransactionFilterLocals[]
  filterValues?: ProductModel[]
  getFilterValues?: () => Promise<ProductModel[]>
}

export const useTransactions = (
  repository: TransactionsRepository,
  resultsPerPage: number,
): IUseTransactions => {
  const { startLoading, stopLoading } = useLoader()
  const { merchant } = useUserData()
  const [transactions, setTransactions] = useState<TransactionModel[]>([])
  const [page, setPage] = useState<number>(1)
  const [count, setCount] = useState<number>(0)
  const [filters, setFilters] = useState<FilterResultModel[]>([] as FilterResultModel[])
  const [localsFilterData, setLocalsFilterData] = useState<TransactionFilterLocals[]>()
  const [filterValues, setFilterValues] = useState<ProductModel[]>([])

  const getFilterValues = async (): Promise<ProductModel[]> => {
    startLoading()
    const response = await TransactionsContract(repository).getFilterValues()
    stopLoading()

    if (!response.data) {
      throw new Error('Failed to fetch filter values')
    }

    const result = Array.isArray(response.data) ? response.data : [response.data]
    setFilterValues(result)
    return result
  }

  const getTransactions = (): void => {
    startLoading()
    merchant.isNursery ?
      TransactionsContract(repository)
        .getGuarderia(page, resultsPerPage, filters)
        .then(response => {
          if (response && response.data && response.data.rows) {
            setTransactions(response.data.rows)
            setCount(response.data.totalRows)
          }
        })
        .finally(() => stopLoading()) :
      TransactionsContract(repository)
        .get(page, resultsPerPage, filters)
        .then(response => {
          if (response && response.data && response.data.rows) {
            setTransactions(response.data.rows)
            setCount(response.data.totalRows)
          }
        })
        .finally(() => stopLoading())
  }

  const convertObjectToArray = (obj: Record<string, string | string[]>): FilterResultModel[] => {
    return Object.keys(obj).map(key => ({
      name: key,
      value: Array.isArray(obj[key]) ? (obj[key] as string[]).join(',') : (obj[key] as string),
    }))
  }

  const handleSearch = (data: TransactionFiltersModel): void => {
    setFilters(convertObjectToArray({ ...data }))
    setPage(1)
  }

  const onPageChange = (page: number): void => {
    setPage(page)
  }

  const clearTransactions = (): void => {
    setTransactions([] as TransactionModel[])
    setFilters([] as FilterResultModel[])
    setPage(1)
    setCount(0)
  }

  const addDatesToFilename = (fileName: string): string => {
    const beginDate = filters.find(filter => filter.name === 'beginDate')?.value
    const endDate = filters.find(filter => filter.name === 'endDate')?.value
    return `${fileName}_${beginDate}_${endDate}`
  }

  const downloadTransactions = (): void => {
    startLoading()
    TransactionsContract(repository)
      .download(filters, merchant.isNursery)
      .then(response => {
        if (response && response.data && response.data.file) {
          if (response.data.extension === 'pdf') {
            textToPdf(
              response.data.file,
              addDatesToFilename(response.data.fileName || 'transacciones'),
            )
          }
          if (response.data.extension === 'xlsx') {
            dataToExcel(
              response.data.file,
              addDatesToFilename(response.data.fileName || 'transacciones'),
            )
          }
        }
      })
      .finally(() => stopLoading())
  }

  const initalFiltersGuarderia = (): void => {
    if (merchant.isNursery) {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      setFilters([
        { name: "beginDate", value: oneYearAgo.toISOString().split('T')[0] },
        { name: "endDate", value: new Date().toISOString().split('T')[0] }
      ])
    }
  }

  useEffect(() => {
    getFilterValues()
      .then(data => setFilterValues(data))
      .catch(error => console.error('Failed to load filter values:', error))
  }, [])

  useEffect(() => {
    TransactionsContract(repository)
      .getLocalsFilter()
      .then(response => {
        if (response && response.data) {
          setLocalsFilterData(response.data)
        }
      })
    initalFiltersGuarderia()
  }, [])

  useEffect(() => {
    if (
      filters.some(filter => filter.name === 'beginDate') &&
      filters.some(filter => filter.name === 'endDate')
    ) {
      getTransactions()
    }
  }, [filters, page])

  return {
    transactions,
    clearTransactions,
    filters,
    handleSearch,
    count,
    page,
    numberOfRecordsPerPage: resultsPerPage,
    onPageChange,
    downloadTransactions,
    localsFilterData,
    filterValues
  }
}
